import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api, ApiError } from '../lib/api';
import { LOG_TYPES, LOG_TYPE_CONFIG, formatDateTime, getRelativeTime } from '../lib/constants';
import { LogEntryScreen, LogHistoryScreen } from './OfficerLog';
import { TasksScreen } from './OfficerTasks';
import { OfficerInstructionsScreen, OfficerPoliciesScreen, OfficerNavigation } from './OfficerInfo';
import OfficerProfile from './OfficerProfile';
import OfficerHR from './OfficerHR';
import PlaybookAlerts from './PlaybookAlerts';
import PatrolScreen, { PatrolHistoryOfficerScreen } from './PatrolScreen';
import { HandoverScreen } from './HandoverScreen';
import OfficerVisitorsScreen from './OfficerVisitors';
import OfficerTenantsScreen from './OfficerTenants';
import {
  HomeIcon, ClipboardDocumentListIcon, MapPinIcon, ClockIcon,
  UserGroupIcon, Cog6ToothIcon, PlusIcon, ArrowRightOnRectangleIcon,
  BuildingOfficeIcon, ChartBarIcon, DocumentTextIcon, BellAlertIcon,
  UsersIcon, EyeIcon, FunnelIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

function OfficerApp({ user }) {
  const location = useLocation();
  const { signOut } = useAuth();
  const [activeShift, setActiveShift] = useState(null);
  const [lastPatrolTime, setLastPatrolTime] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rosterShift, setRosterShift] = useState(null);
  const [noRosterShift, setNoRosterShift] = useState(false);
  const [handover, setHandover] = useState(null);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [handoverAcked, setHandoverAcked] = useState(false);

  const [siteChecks, setSiteChecks] = useState([]);
  const [checksCompleted, setChecksCompleted] = useState(false);
  const [checkStates, setCheckStates] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  async function loadHandover() {
    if (!selectedSite) return;
    setHandoverLoading(true);
    try {
      // Get the actual handover brief from the previous officer
      const pendingRes = await api.handovers.pending(selectedSite.id);
      const handoverBrief = pendingRes.data || null;

      // Also get visitors on site
      let visitors = [];
      try {
        const visRes = await api.visitors.list({ site_id: selectedSite.id, status: 'on_site' });
        visitors = visRes.data || [];
      } catch {}

      // Load site checks
      try {
        const checksRes = await api.siteChecks.list(selectedSite.id);
        setSiteChecks(checksRes.data || []);
      } catch {}

      setHandover({
        brief: handoverBrief,
        prevOfficer: handoverBrief?.author ? `${handoverBrief.author.first_name} ${handoverBrief.author.last_name}` : null,
        visitors,
      });
    } catch (e) { console.error('Handover load failed:', e); setHandover({ brief: null, prevOfficer: null, visitors: [] }); }
    finally { setHandoverLoading(false); }
  }

  async function startShift() {
    if (!selectedSite) return;
    try {
      let lat, lng;
      try {
        const pos = await new Promise((res,rej) => navigator.geolocation.getCurrentPosition(res,rej,{timeout:8000}));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}

      // Find the next SCHEDULED roster shift for this officer at this site
      const todayStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).toISOString();
      const scheduled = await api.shifts.list({ officer_id: user.id, status: 'SCHEDULED', site_id: selectedSite.id });
      const rShift = (scheduled.data || [])
        .filter(s => new Date(s.start_time) >= new Date(todayStart))
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];

      if (!rShift) {
        setNoRosterShift(true);
        return;
      }

      // Activate the roster shift
      const r = await api.shifts.checkin(rShift.id, { lat, lng });
      setActiveShift(r.data);
      setRosterShift(rShift);
    } catch (err) { alert(err.message); }
  }

  async function endShift() {
    if (!activeShift) return;
    try {
      await api.shifts.checkout(activeShift.id);
      setActiveShift(null);
    } catch (err) { console.error(err.message); }
  }

  // Fetch officer data
  useEffect(() => {
    async function fetchOfficerData() {
      try {
        // Get sites assigned to this officer
        const sitesResponse = await api.officerSites.list(user.id);
        setSites(sitesResponse.data || []);

        // Get active shift
        const shiftsResponse = await api.shifts.list({ 
          officer_id: user.id, 
          status: 'ACTIVE' 
        });
        const activeShifts = shiftsResponse.data || [];
        if (activeShifts.length > 0) {
          setActiveShift(activeShifts[0]);
          setSelectedSite(activeShifts[0].site);
        } else {
          // Check for next upcoming scheduled shift — auto-select that site
          try {
            const schedRes = await api.shifts.list({ officer_id: user.id, status: 'SCHEDULED' });
            const upcoming = (schedRes.data || [])
              .filter(s => new Date(s.start_time) >= new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()))
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            if (upcoming.length > 0 && upcoming[0].site) {
              setSelectedSite(upcoming[0].site);
              setRosterShift(upcoming[0]);
            } else if ((sitesResponse.data || []).length === 1) {
              setSelectedSite(sitesResponse.data[0]);
            }
          } catch {}
        }
      } catch (err) {
        console.error('Failed to fetch officer data:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchOfficerData();
  }, [user, loadAttempt]);

  // Load last patrol time for PlaybookAlerts (only from current shift)
  useEffect(() => {
    if (!selectedSite?.id || !activeShift) return;
    const shiftStart = activeShift.checked_in_at || activeShift.start_time;
    api.patrols.listSessions({ site_id: selectedSite.id, status: 'completed', limit: 1 })
      .then(res => {
        const last = (res.data || [])[0];
        if (last?.ended_at && new Date(last.ended_at) >= new Date(shiftStart)) {
          setLastPatrolTime(last.ended_at);
        }
      }).catch(() => {});
  }, [selectedSite?.id, activeShift?.id]);

  // Check for next roster shift on lock screen
  useEffect(() => {
    if (!selectedSite || activeShift) return;
    const todayStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).toISOString();
    api.shifts.list({ officer_id: user.id, status: 'SCHEDULED', site_id: selectedSite.id })
      .then(res => {
        const upcoming = (res.data || [])
          .filter(s => new Date(s.start_time) >= new Date(todayStart))
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        if (upcoming.length > 0) { setRosterShift(upcoming[0]); setNoRosterShift(false); }
        else { setRosterShift(null); setNoRosterShift(true); }
      }).catch(() => setNoRosterShift(true));
  }, [selectedSite, activeShift]);

  if (loading) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0f1623'}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:'1.5rem',fontWeight:800,marginBottom:'1.5rem'}}>
            <span style={{color:'#1a52a8'}}>DOB</span><span style={{color:'#fff'}}> Live</span>
          </div>
          <div className="spinner" style={{borderTopColor:'#1a52a8',borderColor:'rgba(255,255,255,0.1)',width:'2rem',height:'2rem',margin:'0 auto'}}/>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0f1623',padding:'1.5rem'}}>
        <div style={{textAlign:'center',maxWidth:'380px'}}>
          <div style={{fontSize:'1.5rem',fontWeight:800,marginBottom:'1.5rem'}}>
            <span style={{color:'#1a52a8'}}>DOB</span><span style={{color:'#fff'}}> Live</span>
          </div>
          <div style={{background:'#1a2235',borderRadius:'12px',padding:'1.5rem'}}>
            <div style={{fontSize:'1rem',fontWeight:700,color:'#fff',marginBottom:'0.5rem'}}>Connection problem</div>
            <p style={{color:'rgba(255,255,255,0.5)',fontSize:'0.875rem',marginBottom:'1.5rem'}}>
              Could not load your shift data. Check your signal and try again.
            </p>
            <button onClick={() => { setLoadError(false); setLoading(true); setLoadAttempt(a => a+1); }} style={{width:'100%',padding:'0.75rem',background:'#1a52a8',color:'#fff',border:'none',borderRadius:'8px',fontWeight:600,cursor:'pointer',fontSize:'0.9375rem'}}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no site selected and not on site picker page, redirect to site picker (except My Hours and Profile)
  if (!selectedSite && !['/sites', '/my-hours', '/profile'].includes(location.pathname)) {
    return <Navigate to="/sites" replace />;
  }

  // LOCK SCREEN — must go on duty before accessing anything (except My Hours and Profile)
  if (selectedSite && !activeShift && !['/my-hours', '/profile'].includes(location.pathname)) {
    return (
      <div style={{minHeight:'100vh',background:'#0b1222',display:'flex',flexDirection:'column'}}>
        <OfficerHeader user={user} selectedSite={selectedSite} activeShift={null} />
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',padding:'1.5rem'}}>
          <div style={{width:'100%',maxWidth:'360px',textAlign:'center'}}>
            <div style={{width:56,height:56,borderRadius:'12px',background:'rgba(26,82,168,0.15)',border:'1px solid rgba(26,82,168,0.3)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 1.25rem'}}>
              <span style={{fontSize:'1.5rem',fontWeight:800,color:'#1a52a8'}}>RS</span>
            </div>
            <div style={{fontSize:'1.25rem',fontWeight:700,color:'#fff',marginBottom:'0.5rem'}}>
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user.first_name}
            </div>
            <div style={{fontSize:'0.9375rem',color:'rgba(255,255,255,0.5)',marginBottom:'0.25rem'}}>{selectedSite.name}</div>
            {rosterShift && (
              <div style={{fontSize:'0.9375rem',color:'#fff',marginBottom:'0.5rem',fontWeight:600}}>
                Your shift: {new Date(rosterShift.start_time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})} – {rosterShift.end_time ? new Date(rosterShift.end_time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) : '?'}
              </div>
            )}
            <div style={{fontSize:'0.8125rem',color:'rgba(74,222,128,0.6)',marginBottom:'1.5rem',fontStyle:'italic'}}>Have a safe shift</div>
            {noRosterShift && <div style={{padding:'0.75rem',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'8px',marginBottom:'1rem',fontSize:'0.8125rem',color:'#ef4444'}}>No shift scheduled for you at this site. Contact your manager.</div>}

            {/* Step 1: Load handover */}
            {!handover && !handoverLoading && !noRosterShift && (
              <button onClick={loadHandover}
                style={{width:'100%',padding:'18px',background:'rgba(74,222,128,0.15)',border:'2px solid rgba(74,222,128,0.5)',borderRadius:'12px',color:'#4ade80',fontSize:'1.125rem',fontWeight:700,cursor:'pointer'}}>
                GO ON DUTY
              </button>
            )}
            {handoverLoading && <div style={{padding:'1rem',color:'rgba(255,255,255,0.4)',fontSize:'0.875rem'}}>Loading handover...</div>}

            {/* Step 2: Show handover from previous officer */}
            {handover && !handoverAcked && (
              <div style={{textAlign:'left',width:'100%'}}>
                <div style={{fontSize:'0.75rem',fontWeight:700,color:'#3b82f6',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.75rem'}}>Shift Handover</div>

                {handover.brief ? (
                  <>
                    {/* Who handed over */}
                    <div style={{padding:'0.75rem',background:'rgba(59,130,246,0.08)',border:'1px solid rgba(59,130,246,0.2)',borderRadius:'8px',marginBottom:'0.625rem',fontSize:'0.8125rem'}}>
                      <div style={{fontWeight:600,color:'#60a5fa',marginBottom:'0.25rem'}}>Handover from {handover.prevOfficer}</div>
                      <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.35)'}}>{new Date(handover.brief.created_at).toLocaleDateString('en-GB',{weekday:'short',day:'2-digit',month:'short',timeZone:'Europe/London'})} at {new Date(handover.brief.created_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
                    </div>

                    {/* Site status */}
                    <div style={{padding:'0.75rem',background: handover.brief.site_status === 'ALL_SECURE' ? 'rgba(74,222,128,0.08)' : handover.brief.site_status === 'REQUIRES_ATTENTION' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
                      border:`1px solid ${handover.brief.site_status === 'ALL_SECURE' ? 'rgba(74,222,128,0.2)' : handover.brief.site_status === 'REQUIRES_ATTENTION' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,borderRadius:'8px',marginBottom:'0.625rem'}}>
                      <div style={{fontSize:'0.875rem',fontWeight:700,color: handover.brief.site_status === 'ALL_SECURE' ? '#4ade80' : handover.brief.site_status === 'REQUIRES_ATTENTION' ? '#ef4444' : '#f59e0b'}}>
                        Site: {handover.brief.site_status === 'ALL_SECURE' ? 'All Secure' : handover.brief.site_status === 'REQUIRES_ATTENTION' ? 'Requires Attention' : 'Issues Noted'}
                      </div>
                    </div>

                    {/* Equipment */}
                    <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.625rem'}}>
                      <div style={{flex:1,padding:'0.625rem',background: handover.brief.keys_handed_over ? 'rgba(74,222,128,0.08)' : 'rgba(239,68,68,0.08)',border:`1px solid ${handover.brief.keys_handed_over ? 'rgba(74,222,128,0.2)' : 'rgba(239,68,68,0.2)'}`,borderRadius:'8px',textAlign:'center',fontSize:'0.8125rem',fontWeight:600,color: handover.brief.keys_handed_over ? '#4ade80' : '#ef4444'}}>
                        {handover.brief.keys_handed_over ? '✓' : '✗'} Keys
                      </div>
                      <div style={{flex:1,padding:'0.625rem',background: handover.brief.radio_handed_over ? 'rgba(74,222,128,0.08)' : 'rgba(239,68,68,0.08)',border:`1px solid ${handover.brief.radio_handed_over ? 'rgba(74,222,128,0.2)' : 'rgba(239,68,68,0.2)'}`,borderRadius:'8px',textAlign:'center',fontSize:'0.8125rem',fontWeight:600,color: handover.brief.radio_handed_over ? '#4ade80' : '#ef4444'}}>
                        {handover.brief.radio_handed_over ? '✓' : '✗'} Radio
                      </div>
                    </div>

                    {/* Outstanding issues */}
                    {handover.brief.outstanding_issues && (
                      <div style={{padding:'0.75rem',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:'8px',marginBottom:'0.625rem',fontSize:'0.8125rem'}}>
                        <div style={{fontWeight:600,color:'#f59e0b',marginBottom:'0.25rem'}}>Outstanding Issues</div>
                        <div style={{color:'rgba(255,255,255,0.6)',lineHeight:1.5,whiteSpace:'pre-wrap'}}>{handover.brief.outstanding_issues}</div>
                      </div>
                    )}

                    {/* Key points */}
                    <div style={{padding:'0.75rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'8px',marginBottom:'0.625rem',fontSize:'0.8125rem'}}>
                      <div style={{fontWeight:600,color:'#fff',marginBottom:'0.25rem'}}>Key Points</div>
                      <div style={{color:'rgba(255,255,255,0.6)',lineHeight:1.5,whiteSpace:'pre-wrap'}}>{handover.brief.content}</div>
                    </div>
                  </>
                ) : (
                  <div style={{padding:'1rem',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:'8px',marginBottom:'0.625rem',textAlign:'center'}}>
                    <div style={{fontSize:'0.875rem',fontWeight:600,color:'#f59e0b'}}>No handover submitted</div>
                    <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)',marginTop:'0.25rem'}}>The previous officer did not submit a handover report.</div>
                  </div>
                )}

                {/* Visitors on site */}
                {handover.visitors.length > 0 && (
                  <div style={{padding:'0.75rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'8px',marginBottom:'0.625rem',fontSize:'0.8125rem'}}>
                    <div style={{fontWeight:600,color:'#fff',marginBottom:'0.25rem'}}>Visitors On Site ({handover.visitors.length})</div>
                    {handover.visitors.map((v,i) => <div key={i} style={{color:'rgba(255,255,255,0.5)',marginTop:'0.25rem'}}>• {v.visitor_name || v.name}</div>)}
                  </div>
                )}

                {/* Acknowledge */}
                <button onClick={async () => {
                  // Acknowledge the handover record
                  if (handover.brief?.id) {
                    try { await api.handovers.acknowledge(handover.brief.id); } catch {}
                  }
                  // Log it
                  api.logs.create({ site_id: selectedSite.id, log_type: 'GENERAL', title: `Handover Acknowledged — ${user.first_name} ${user.last_name}`, description: `Incoming officer acknowledged shift handover from ${handover.prevOfficer || 'previous shift'}.`, occurred_at: new Date().toISOString(), type_data: { shift_event: 'HANDOVER_ACK', prev_officer: handover.prevOfficer, handover_id: handover.brief?.id } }).catch(() => {});
                  setHandoverAcked(true);
                }} style={{width:'100%',padding:'16px',background:'rgba(59,130,246,0.15)',border:'2px solid rgba(59,130,246,0.5)',borderRadius:'12px',color:'#60a5fa',fontSize:'1rem',fontWeight:700,cursor:'pointer',marginTop:'0.5rem'}}>
                  I ACKNOWLEDGE THIS HANDOVER
                </button>
              </div>
            )}

            {/* Step 3: Site checks (if configured) */}
            {handoverAcked && siteChecks.length > 0 && !checksCompleted && (
              <div style={{textAlign:'left',width:'100%'}}>
                <div style={{fontSize:'0.75rem',fontWeight:700,color:'#f59e0b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.75rem'}}>Start-of-Shift Checks</div>
                <div style={{fontSize:'0.8125rem',color:'rgba(255,255,255,0.4)',marginBottom:'0.75rem'}}>Complete all checks before going on duty.</div>
                <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',marginBottom:'1rem'}}>
                  {siteChecks.map(c => (
                    <button key={c.id} onClick={() => setCheckStates(prev => ({...prev, [c.id]: !prev[c.id]}))}
                      style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.875rem',background: checkStates[c.id] ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.04)',
                        border:`1.5px solid ${checkStates[c.id] ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)'}`,borderRadius:'8px',cursor:'pointer',width:'100%',textAlign:'left'}}>
                      <div style={{width:'22px',height:'22px',borderRadius:'50%',background: checkStates[c.id] ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.06)',border:`1.5px solid ${checkStates[c.id] ? '#4ade80' : 'rgba(255,255,255,0.15)'}`,
                        display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'0.75rem',color:'#4ade80',fontWeight:700}}>
                        {checkStates[c.id] ? '✓' : ''}
                      </div>
                      <span style={{fontSize:'0.875rem',fontWeight:600,color: checkStates[c.id] ? '#4ade80' : '#fff'}}>{c.label}</span>
                    </button>
                  ))}
                </div>
                {siteChecks.every(c => checkStates[c.id]) ? (
                  <button onClick={async () => {
                    // Save completed checks
                    setChecksCompleted(true);
                  }} style={{width:'100%',padding:'16px',background:'rgba(74,222,128,0.15)',border:'2px solid rgba(74,222,128,0.5)',borderRadius:'12px',color:'#4ade80',fontSize:'1rem',fontWeight:700,cursor:'pointer'}}>
                    ALL CHECKS COMPLETE — CONTINUE
                  </button>
                ) : (
                  <div style={{textAlign:'center',fontSize:'0.8125rem',color:'rgba(255,255,255,0.3)'}}>
                    {Object.values(checkStates).filter(Boolean).length}/{siteChecks.length} checks completed
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Go on duty */}
            {handoverAcked && (siteChecks.length === 0 || checksCompleted) && (
              <button onClick={async () => {
                // Save site checks if any
                if (siteChecks.length > 0 && activeShift?.id) {
                  try {
                    await api.siteChecks.complete(activeShift.id, siteChecks.map(c => ({ check_id: c.id })));
                  } catch {}
                }
                startShift();
              }}
                style={{width:'100%',padding:'18px',background:'rgba(74,222,128,0.15)',border:'2px solid rgba(74,222,128,0.5)',borderRadius:'12px',color:'#4ade80',fontSize:'1.125rem',fontWeight:700,cursor:'pointer'}}>
                GO ON DUTY
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="officer-shell">
      <PlaybookAlerts user={user} site={selectedSite} shift={activeShift} lastPatrolTime={lastPatrolTime} onTaskDismissed={() => setLastPatrolTime(new Date().toISOString())} />
      <OfficerHeader user={user} selectedSite={selectedSite} activeShift={activeShift} />
      <div className="officer-content">

      <Routes>
        <Route path="/sites" element={
          activeShift ? <Navigate to="/" replace /> :
          <SitePickerScreen
            sites={sites}
            onSiteSelect={(site) => { setSelectedSite(site); }}
            user={user}
          />
        } />
        <Route path="/" element={
          <OfficerDashboard
            user={user}
            site={selectedSite}
            shift={activeShift}
            onStartShift={startShift}
            onEndShift={endShift}
            onPatrolTimeUpdate={setLastPatrolTime}
          />
        } />
        <Route path="/log" element={
          <LogEntryScreen 
            user={user}
            site={selectedSite}
            shift={activeShift}
          />
        } />
        <Route path="/logs" element={
          <LogHistoryScreen 
            user={user}
            site={selectedSite}
          />
        } />
        <Route path="/assignments" element={
          <TasksScreen 
            user={user}
            site={selectedSite}
            shift={activeShift}
          />
        } />
        <Route path="/instructions" element={<OfficerInstructionsScreen user={user} site={selectedSite} />} />
        <Route path="/patrol"   element={<PatrolScreen user={user} site={selectedSite} shift={activeShift} onPatrolEnded={() => setLastPatrolTime(new Date().toISOString())} />} />
        <Route path="/patrol-history" element={<PatrolHistoryOfficerScreen user={user} site={selectedSite} />} />
        <Route path="/handover" element={<HandoverScreen user={user} site={selectedSite} shift={activeShift} onShiftEnded={() => { setActiveShift(null); }} />} />
        <Route path="/visitors" element={<OfficerVisitorsScreen site={selectedSite} />} />
        <Route path="/contacts" element={<OfficerTenantsScreen site={selectedSite} />} />
          <Route path="/profile" element={<OfficerProfile user={user} />} />
        <Route path="/my-hours" element={<OfficerHR user={user} />} />
        <Route path="/policies" element={<OfficerPoliciesScreen user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </div>
      <OfficerNavigation onSignOut={async () => {
        if (activeShift) {
          if (!confirm('Are you sure you want to end your shift and sign out?')) return;
          try {
            // Check if shift was already expired by cron
            const check = await api.shifts.list({ officer_id: user.id, status: 'ACTIVE' });
            if (check.data && check.data.length > 0) {
              await api.shifts.checkout(check.data[0].id);
            }
          } catch (err) { console.error('Checkout failed:', err); }
          setActiveShift(null);
        }
        signOut();
      }} />

    </div>
  );
}

// Officer Header
function OfficerHeader({ user, selectedSite, activeShift, onSignOut }) {
  return (
    <div className="officer-header">
      {user.logo_url ? (
        <img src={user.logo_url} alt="Logo" style={{maxHeight:'32px',maxWidth:'120px',objectFit:'contain',flexShrink:0}} />
      ) : (
        <div className="logo" style={{flexShrink:0}}><span className="dob">DOB</span><span className="live"> Live</span></div>
      )}
      <div style={{textAlign:'right',minWidth:0,overflow:'hidden'}}>
        {selectedSite && <div style={{fontSize:'0.8125rem',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'160px'}}>{selectedSite.name}</div>}
        <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.5)'}}>{user.first_name} {user.last_name}</div>
      </div>
    </div>
  );
}


function SitePickerScreen({ sites, onSiteSelect, user }) {
  const navigate = useNavigate();
  
  const handleSiteSelect = (site) => {
    onSiteSelect(site);
    navigate('/');
  };
  
  return (
    <div style={{padding:'1.25rem',paddingBottom:'5rem'}}>
      <div style={{marginBottom:'1.5rem'}}>
        <h2 style={{fontSize:'1.125rem',fontWeight:700,color:'#fff',marginBottom:'0.25rem'}}>Select Your Site</h2>
        <p style={{fontSize:'0.8125rem',color:'rgba(255,255,255,0.4)'}}>Choose which site you are working at today</p>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
        {sites.length === 0 ? (
          <div style={{textAlign:'center',padding:'3rem 1rem',color:'rgba(255,255,255,0.3)'}}>
            <MapPinIcon style={{width:'2.5rem',height:'2.5rem',margin:'0 auto 0.75rem'}} />
            <p style={{fontSize:'0.875rem'}}>No sites assigned. Contact your manager.</p>
          </div>
        ) : (
          sites.map((site) => (
            <button
              key={site.id}
              onClick={() => handleSiteSelect(site)}
              style={{background:'#1a2235',border:'1.5px solid rgba(255,255,255,0.08)',borderRadius:'10px',padding:'1rem',textAlign:'left',cursor:'pointer',width:'100%',transition:'border-color 0.15s'}}
              onMouseEnter={e=>e.currentTarget.style.borderColor='var(--blue)'}
              onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'}
            >
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.75rem'}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:'1rem',fontWeight:600,color:'#fff',marginBottom:'0.25rem'}}>{site.name}</div>
                  {site.address && <div style={{fontSize:'0.8125rem',color:'rgba(255,255,255,0.4)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{site.address}</div>}
                </div>
                <ArrowRightOnRectangleIcon style={{width:'1.25rem',height:'1.25rem',color:'rgba(255,255,255,0.3)',flexShrink:0}} />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// Officer Dashboard
function OfficerDashboard({ user, site, shift, onStartShift, onEndShift, onPatrolTimeUpdate }) {
  const [recentLogs, setRecentLogs] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [completedTaskIds, setCompletedTaskIds] = useState(new Set());
  const [taskModal, setTaskModal] = useState(null);
  const [taskNotes, setTaskNotes] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [showCheckCall, setShowCheckCall] = useState(false);
  const [checkPin, setCheckPin] = useState('');
  const [checkCallSubmitting, setCheckCallSubmitting] = useState(false);
  const [checkCallDue, setCheckCallDue] = useState(false);
  const [lastCheckCall, setLastCheckCall] = useState(null);
  const [checkCallConfirmed, setCheckCallConfirmed] = useState(false);
  const [checkCallMinsLeft, setCheckCallMinsLeft] = useState(null);
  const [activePatrol, setActivePatrol] = useState(false);
  const [showLogMenu, setShowLogMenu] = useState(false);
  const [clientTasks, setClientTasks] = useState([]);
  const [onSiteVisitors, setOnSiteVisitors] = useState([]);
  const [clientTaskModal, setClientTaskModal] = useState(null);
  const [clientTaskNotes, setClientTaskNotes] = useState('');
  const [clientTaskSubmitting, setClientTaskSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [historyDate, setHistoryDate] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

        // Get recent logs for this officer at this site
        const [logsResponse, todayResponse, tasksResponse, playbookRes] = await Promise.all([
          api.logs.list({ site_id: site?.id, limit: 20, officer_id: user.id }),
          api.logs.list({ site_id: site?.id, officer_id: user.id, from: todayStart, limit: 500 }),
          api.tasks.list({ site_id: site?.id, status: 'PENDING' }),
          api.playbooks.get(site?.id).catch(() => ({ tasks: [] })),
        ]);
        setRecentLogs(logsResponse.data || []);
        setTodayCount((todayResponse.data || []).length);
        setTasks(tasksResponse.data || []);
        // Filter scheduled tasks for today's day of week
        const today = new Date().getDay();
        const todayTasks = (playbookRes.tasks || []).filter(t => !t.days_of_week || t.days_of_week.includes(today));
        setScheduledTasks(todayTasks);
        // Check which tasks were completed today
        const done = new Set();
        (todayResponse.data || []).forEach(l => { if (l.type_data?.scheduled_task_id) done.add(l.type_data.scheduled_task_id); });
        setCompletedTaskIds(done);
        // Set last check call from server data — only from current shift
        const shiftStart = shift?.checked_in_at || shift?.start_time;
        const lastSafetyCheck = (todayResponse.data || []).find(l => l.type_data?.check_call === true && !l.type_data?.missed_check && shiftStart && new Date(l.occurred_at) >= new Date(shiftStart));
        if (lastSafetyCheck) setLastCheckCall(new Date(lastSafetyCheck.occurred_at));
        // Load client tasks for this site
        try {
          const clientTasksRes = await api.alerts.list({ site_id: site.id, status: 'open' });
          setClientTasks(clientTasksRes.data || []);
        } catch {}
        try {
          const visRes = await api.visitors.list({ site_id: site.id, status: 'on_site' });
          setOnSiteVisitors(visRes.data || []);
        } catch {}
        // Check for active patrol + last completed patrol time
        try {
          const patrolRes = await api.patrols.activeSession(site.id);
          setActivePatrol(!!patrolRes.data);
        } catch { setActivePatrol(false); }
        // Load last completed patrol session time
        try {
          const sessionsRes = await api.patrols.listSessions({ site_id: site.id, status: 'completed', limit: 1 });
          const lastSession = (sessionsRes.data || [])[0];
          if (lastSession?.ended_at) onPatrolTimeUpdate?.(lastSession.ended_at);
        } catch {}

      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
        setLastUpdated(new Date());
      }
    }

    if (site) {
      fetchDashboardData();
      // Auto-refresh every 60s
      const interval = setInterval(fetchDashboardData, 60000);
      return () => clearInterval(interval);
    }
  }, [site, user]);

  // Hourly check call timer — ticks every 15 seconds for smooth countdown
  useEffect(() => {
    if (!shift) { setCheckCallDue(false); setCheckCallMinsLeft(null); return; }
    let audioPlayed = false;
    const check = () => {
      const last = lastCheckCall || (shift.checked_in_at ? new Date(shift.checked_in_at) : new Date());
      const elapsed = (Date.now() - last.getTime()) / 60000;
      const remaining = Math.max(0, Math.ceil(60 - elapsed));
      setCheckCallMinsLeft(remaining);
      if (elapsed >= 55 && elapsed < 40000) {
        setCheckCallDue(true);
        if (!audioPlayed) {
          audioPlayed = true;
          try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const o = ctx.createOscillator(); o.frequency.value = 800; o.connect(ctx.destination); o.start(); setTimeout(() => o.stop(), 500); } catch {}
        }
      } else {
        setCheckCallDue(false);
        audioPlayed = false;
      }
    };
    check(); // run immediately
    const t = setInterval(check, 15000); // tick every 15 seconds
    return () => clearInterval(t);
  }, [shift, lastCheckCall]);

  // Fetch logs for selected history date
  useEffect(() => {
    if (!historyDate || !site) { setHistoryLogs([]); return; }
    setHistoryLoading(true);
    const from = new Date(historyDate + 'T00:00:00').toISOString();
    const to = new Date(historyDate + 'T23:59:59').toISOString();
    api.logs.list({ site_id: site.id, officer_id: user.id, from, to, limit: 100 })
      .then(res => setHistoryLogs(res.data || []))
      .catch(() => setHistoryLogs([]))
      .finally(() => setHistoryLoading(false));
  }, [historyDate, site?.id]);
  
  if (!site) {
    return <Navigate to="/sites" replace />;
  }
  
  return (
    <div style={{padding:'1rem',paddingBottom:'5rem'}}>
      {/* Shift status */}
      {shift ? (
        <div style={{background:'rgba(74,222,128,0.1)',border:'1px solid rgba(74,222,128,0.25)',borderRadius:'10px',padding:'0.875rem 1rem',display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.75rem'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:'#4ade80',animation:'pulse 2s infinite'}} />
              <span style={{fontSize:'0.8125rem',fontWeight:700,color:'#4ade80'}}>On Duty</span>
              <span style={{color:'rgba(255,255,255,0.3)'}}>—</span>
              <span style={{fontSize:'0.8125rem',color:'rgba(255,255,255,0.6)'}}>{site?.name}</span>
            </div>
            <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.35)',marginTop:'0.25rem'}}>
              {new Date().toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short',timeZone:'Europe/London'})}
            </div>
          </div>
        </div>
      ) : (
        <div style={{marginBottom:'0.75rem'}}>
        </div>
      )}

      {/* Client Tasks */}
      {clientTasks.length > 0 && (
        <div style={{marginBottom:'0.625rem'}}>
          {clientTasks.map(task => (
            <div key={task.id} style={{padding:'0.875rem',background:'rgba(59,130,246,0.08)',border:'2px solid rgba(59,130,246,0.3)',borderRadius:'10px',marginBottom:'0.5rem'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'0.5rem'}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:'0.625rem',fontWeight:700,color:'#60a5fa',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.25rem'}}>Client Task</div>
                  <div style={{fontSize:'0.9375rem',fontWeight:700,color:'#fff'}}>{task.title}</div>
                  {task.description && <div style={{fontSize:'0.8125rem',color:'rgba(255,255,255,0.5)',marginTop:'0.25rem',lineHeight:1.4}}>{task.description}</div>}
                  <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.3)',marginTop:'0.375rem'}}>{new Date(task.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
                </div>
              </div>
              <div style={{display:'flex',gap:'0.5rem',marginTop:'0.625rem'}}>
                <button onClick={() => { setClientTaskModal({ task, outcome:'complete' }); setClientTaskNotes(''); }}
                  style={{flex:1,padding:'0.625rem',background:'rgba(74,222,128,0.12)',border:'1.5px solid rgba(74,222,128,0.3)',borderRadius:'8px',color:'#4ade80',fontSize:'0.8125rem',fontWeight:700,cursor:'pointer'}}>
                  Complete
                </button>
                <button onClick={() => { setClientTaskModal({ task, outcome:'unsuccessful' }); setClientTaskNotes(''); }}
                  style={{flex:1,padding:'0.625rem',background:'rgba(239,68,68,0.08)',border:'1.5px solid rgba(239,68,68,0.25)',borderRadius:'8px',color:'#ef4444',fontSize:'0.8125rem',fontWeight:700,cursor:'pointer'}}>
                  Unsuccessful
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Task Response Modal */}
      {clientTaskModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#0f1929',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',padding:'1.5rem',width:'100%',maxWidth:'360px'}}>
            <div style={{fontSize:'15px',fontWeight:700,color: clientTaskModal.outcome==='complete' ? '#4ade80' : '#ef4444',marginBottom:'4px'}}>
              {clientTaskModal.outcome==='complete' ? 'Task Complete' : 'Task Unsuccessful'}
            </div>
            <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'12px'}}>{clientTaskModal.task.title}</div>
            <div style={{fontSize:'10px',fontWeight:700,color:'rgba(255,255,255,0.3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'6px'}}>
              {clientTaskModal.outcome==='complete' ? 'Comments (optional)' : 'Reason *'}
            </div>
            <textarea value={clientTaskNotes} onChange={e => setClientTaskNotes(e.target.value)} rows={3}
              placeholder={clientTaskModal.outcome==='complete' ? 'Any notes on what was done...' : 'Why could this task not be completed?'}
              style={{width:'100%',padding:'10px 12px',background:'rgba(255,255,255,0.05)',border:'1.5px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#fff',fontSize:'14px',resize:'none',boxSizing:'border-box',fontFamily:'inherit'}} />
            <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
              <button onClick={() => setClientTaskModal(null)}
                style={{flex:1,padding:'13px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'rgba(255,255,255,0.6)',fontSize:'14px',fontWeight:600,cursor:'pointer'}}>
                Cancel
              </button>
              <button disabled={clientTaskSubmitting || (clientTaskModal.outcome==='unsuccessful' && !clientTaskNotes.trim())}
                onClick={async () => {
                  setClientTaskSubmitting(true);
                  try {
                    const name = `${user.first_name} ${user.last_name}`;
                    const existing = clientTaskModal.task.description || '';
                    let responses = [];
                    try { const p = JSON.parse(existing); if (Array.isArray(p)) responses = p; } catch {}
                    responses.push({
                      from: 'officer', name, createdAt: new Date().toISOString(),
                      text: clientTaskModal.outcome==='complete'
                        ? `✓ Task completed${clientTaskNotes.trim() ? ': ' + clientTaskNotes.trim() : ''}`
                        : `✗ Unsuccessful: ${clientTaskNotes.trim()}`,
                    });
                    const status = clientTaskModal.outcome==='complete' ? 'resolved' : 'unsuccessful';
                    await api.alerts.update(clientTaskModal.task.id, { status, description: JSON.stringify(responses) });
                    setClientTasks(prev => prev.filter(t => t.id !== clientTaskModal.task.id));
                    setClientTaskModal(null);
                  } catch {}
                  setClientTaskSubmitting(false);
                }}
                style={{flex:1,padding:'13px',background: clientTaskModal.outcome==='complete' ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
                  border:`1.5px solid ${clientTaskModal.outcome==='complete' ? 'rgba(74,222,128,0.4)' : 'rgba(239,68,68,0.4)'}`,
                  borderRadius:'10px',color: clientTaskModal.outcome==='complete' ? '#4ade80' : '#ef4444',fontSize:'14px',fontWeight:600,cursor:'pointer',
                  opacity: (clientTaskSubmitting || (clientTaskModal.outcome==='unsuccessful' && !clientTaskNotes.trim())) ? 0.5 : 1}}>
                {clientTaskSubmitting ? '...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Primary actions */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.625rem',marginBottom:'0.625rem'}}>
        <Link to="/patrol" className="officer-action-btn" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',fontSize:'0.9375rem',marginBottom:0,background: activePatrol ? 'rgba(251,191,36,0.15)' : 'rgba(74,222,128,0.12)',borderColor: activePatrol ? 'rgba(251,191,36,0.4)' : 'rgba(74,222,128,0.3)',color: activePatrol ? '#fbbf24' : '#4ade80'}}>
          <MapPinIcon style={{width:'1.125rem',height:'1.125rem'}} />
          {activePatrol ? 'Resume Patrol' : 'Start Patrol'}
        </Link>
        <Link to="/log?type=INCIDENT" className="officer-action-btn" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',fontSize:'0.9375rem',marginBottom:0,background:'rgba(239,68,68,0.12)',borderColor:'rgba(239,68,68,0.3)',color:'#ef4444'}}>
          Log an Incident
        </Link>
        <button onClick={() => setShowLogMenu(!showLogMenu)} className="officer-action-btn" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',fontSize:'0.9375rem',marginBottom:0,background: showLogMenu ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)',borderColor:'rgba(59,130,246,0.25)',color:'#60a5fa',cursor:'pointer'}}>
          <ClipboardDocumentListIcon style={{width:'1.125rem',height:'1.125rem'}} />
          Occurrence Log
        </button>
        <Link to="/log?type=VEHICLE" className="officer-action-btn" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',fontSize:'0.9375rem',marginBottom:0,background:'rgba(139,92,246,0.12)',borderColor:'rgba(139,92,246,0.3)',color:'#a78bfa'}}>
          Vehicle Report
        </Link>
      </div>

      {/* Occurrence Log menu */}
      {showLogMenu && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',marginBottom:'0.625rem',padding:'0.75rem',background:'rgba(59,130,246,0.05)',border:'1px solid rgba(59,130,246,0.15)',borderRadius:'10px'}}>
          <Link to="/log?type=EHS" onClick={() => setShowLogMenu(false)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',padding:'0.75rem',background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:'8px',color:'#f59e0b',fontSize:'0.8125rem',fontWeight:700,textDecoration:'none'}}>
            EH&S
          </Link>
          <Link to="/log?type=CCTV_CHECK" onClick={() => setShowLogMenu(false)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',padding:'0.75rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'rgba(255,255,255,0.6)',fontSize:'0.8125rem',fontWeight:700,textDecoration:'none'}}>
            CCTV Patrol
          </Link>
          <Link to="/log?type=VISITOR" onClick={() => setShowLogMenu(false)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',padding:'0.75rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'rgba(255,255,255,0.6)',fontSize:'0.8125rem',fontWeight:700,textDecoration:'none'}}>
            Visitor / Contractor
          </Link>
          <Link to="/log?type=GENERAL_INFO" onClick={() => setShowLogMenu(false)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.375rem',padding:'0.75rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'rgba(255,255,255,0.6)',fontSize:'0.8125rem',fontWeight:700,textDecoration:'none'}}>
            Gen Info
          </Link>
        </div>
      )}

      {/* Check Call + Panic */}
      {shift && (
        <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.625rem'}}>
          <button onClick={() => { setShowCheckCall(true); setCheckPin(''); }}
            style={{flex:2,padding:'0.875rem',background: checkCallDue ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.1)',border:`2px solid ${checkCallDue ? 'rgba(239,68,68,0.5)' : 'rgba(59,130,246,0.25)'}`,borderRadius:'10px',color: checkCallDue ? '#ef4444' : '#60a5fa',fontSize:'0.9375rem',fontWeight:700,cursor:'pointer',animation: checkCallDue ? 'pulse 1s infinite' : 'none'}}>
            📞 {checkCallDue ? 'SAFETY CHECK DUE NOW' : checkCallMinsLeft != null ? `Safety Check — ${checkCallMinsLeft} min${checkCallMinsLeft !== 1 ? 's' : ''}` : 'Safety Check'}
          </button>
          <button onClick={async () => {
            if (!confirm('ACTIVATE PANIC ALERT? This will immediately alert the control room.')) return;
            let lat, lng;
            try { const p = await new Promise((r,j) => navigator.geolocation.getCurrentPosition(r,j,{timeout:5000})); lat = p.coords.latitude; lng = p.coords.longitude; } catch {}
            try { await api.escalation.panic({ site_id: site?.id, shift_id: shift?.id, lat, lng }); } catch {}
          }} style={{flex:1,padding:'0.875rem',background:'rgba(239,68,68,0.2)',border:'2px solid rgba(239,68,68,0.5)',borderRadius:'10px',color:'#ef4444',fontSize:'0.9375rem',fontWeight:700,cursor:'pointer'}}>
            🚨 SOS
          </button>
        </div>
      )}

      {/* Check Call PIN Modal */}
      {showCheckCall && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#0f1929',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',padding:'1.5rem',width:'100%',maxWidth:'320px',textAlign:'center'}}>
            <div style={{fontSize:'16px',fontWeight:700,color:'#fff',marginBottom:'4px'}}>Safety Check</div>
            <div style={{fontSize:'13px',color:'rgba(255,255,255,0.4)',marginBottom:'20px'}}>Enter your PIN to confirm</div>
            <input type="text" inputMode="numeric" maxLength={4} value={checkPin} onChange={e => setCheckPin(e.target.value.replace(/\D/g,''))}
              autoFocus placeholder="● ● ● ●"
              style={{width:'100%',background:'rgba(255,255,255,0.07)',border:'1.5px solid rgba(59,130,246,0.3)',borderRadius:'10px',padding:'16px',fontSize:'28px',color:'#fff',textAlign:'center',letterSpacing:'0.5em',boxSizing:'border-box',fontFamily:'monospace'}} />
            <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
              <button onClick={() => setShowCheckCall(false)}
                style={{flex:1,padding:'13px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'rgba(255,255,255,0.6)',fontSize:'14px',fontWeight:600,cursor:'pointer'}}>Cancel</button>
              <button disabled={checkPin.length !== 4 || checkCallSubmitting} onClick={async () => {
                setCheckCallSubmitting(true);
                let lat, lng;
                try { const p = await new Promise((r,j) => navigator.geolocation.getCurrentPosition(r,j,{timeout:5000})); lat = p.coords.latitude; lng = p.coords.longitude; } catch {}
                try {
                  await api.escalation.checkCall({ pin: checkPin, site_id: site?.id, shift_id: shift?.id, lat, lng });
                  setLastCheckCall(new Date());
                  setCheckCallDue(false);
                  setShowCheckCall(false);
                  setCheckCallConfirmed(true);
                  setTimeout(() => setCheckCallConfirmed(false), 3000);
                } catch (e) {
                  alert(e.message || 'Invalid PIN');
                }
                finally { setCheckCallSubmitting(false); }
              }} style={{flex:2,padding:'13px',background:'rgba(74,222,128,0.15)',border:'1.5px solid rgba(74,222,128,0.4)',borderRadius:'10px',color:'#4ade80',fontSize:'14px',fontWeight:700,cursor:'pointer',opacity: checkPin.length === 4 ? 1 : 0.5}}>
                {checkCallSubmitting ? 'Verifying...' : 'Confirm'}
              </button>
            </div>
            {!user.safe_pin && (
              <div style={{marginTop:'12px',fontSize:'11px',color:'#f59e0b'}}>No PIN set — go to Profile to set your safe & duress PINs</div>
            )}
          </div>
        </div>
      )}

      {/* Safety Check Confirmation */}
      {checkCallConfirmed && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#0f1929',borderRadius:'16px',padding:'2rem 3rem',textAlign:'center'}}>
            <div style={{fontSize:'3rem',marginBottom:'0.5rem'}}>✓</div>
            <div style={{fontSize:'18px',fontWeight:700,color:'#4ade80',marginBottom:'4px'}}>Safety Check Confirmed</div>
            <div style={{fontSize:'13px',color:'rgba(255,255,255,0.4)'}}>Logged at {new Date().toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
          </div>
        </div>
      )}

      {/* On Site Now */}
      <Link to="/visitors" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0.875rem',background: onSiteVisitors.length > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.04)',border:`1px solid ${onSiteVisitors.length > 0 ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.08)'}`,borderRadius:'10px',textDecoration:'none',marginBottom:'0.625rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
          <span style={{fontSize:'1.25rem'}}>👥</span>
          <div>
            <div style={{fontSize:'0.875rem',fontWeight:600,color: onSiteVisitors.length > 0 ? '#f59e0b' : 'rgba(255,255,255,0.5)'}}>
              {onSiteVisitors.length > 0 ? `${onSiteVisitors.length} Visitor${onSiteVisitors.length!==1?'s':''} On Site` : 'No Visitors On Site'}
            </div>
            {onSiteVisitors.length > 0 && <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.35)'}}>Remember to sign out before end of shift</div>}
          </div>
        </div>
        {onSiteVisitors.length > 0 && <span style={{background:'#f59e0b',color:'#fff',fontSize:'0.75rem',fontWeight:700,padding:'0.125rem 0.5rem',borderRadius:'8px'}}>{onSiteVisitors.length}</span>}
      </Link>

      {/* Site Contacts */}
      <Link to="/contacts" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0.875rem',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'10px',textDecoration:'none',marginBottom:'0.625rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
          <span style={{fontSize:'1.25rem'}}>📇</span>
          <div>
            <div style={{fontSize:'0.875rem',fontWeight:600,color:'rgba(255,255,255,0.5)'}}>Site Contacts</div>
            <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.35)'}}>Tenant & emergency contacts</div>
          </div>
        </div>
        <span style={{color:'rgba(255,255,255,0.2)',fontSize:'1rem'}}>›</span>
      </Link>

      {/* Today's scheduled tasks */}
      {scheduledTasks.length > 0 && (
        <div style={{marginBottom:'1rem'}}>
          <div style={{fontSize:'0.6875rem',fontWeight:600,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Today's Tasks</div>
          <div style={{display:'flex',flexDirection:'column',gap:'0.375rem'}}>
            {scheduledTasks.map(t => {
              const done = completedTaskIds.has(t.id);
              return (
                <div key={t.id} onClick={() => { if (!done) { setTaskModal(t); setTaskNotes(''); } }}
                  style={{display:'flex',alignItems:'center',gap:'0.625rem',padding:'0.625rem 0.75rem',background: done ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.04)',border:`1px solid ${done ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.08)'}`,borderRadius:'8px',cursor: done ? 'default' : 'pointer'}}>
                  <div style={{width:24,height:24,borderRadius:'50%',background: done ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'12px',color: done ? '#10b981' : 'rgba(255,255,255,0.3)',fontWeight:700}}>
                    {done ? '✓' : '○'}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'0.875rem',fontWeight:600,color: done ? 'rgba(255,255,255,0.5)' : '#fff'}}>{t.name}</div>
                    <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.35)'}}>
                      {t.scheduled_time ? t.scheduled_time.slice(0,5) : 'Shift start'}
                      {t.description && <span style={{marginLeft:'0.5rem'}}>· {t.description}</span>}
                      {done && <span style={{color:'#10b981',marginLeft:'0.5rem'}}>Done</span>}
                    </div>
                  </div>
                  {!done && <span style={{color:'rgba(255,255,255,0.2)',fontSize:'1rem'}}>›</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Task completion modal */}
      {taskModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#0f1929',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',padding:'1.5rem',width:'100%',maxWidth:'360px'}}>
            <div style={{fontSize:'16px',fontWeight:700,color:'#fff',marginBottom:'4px'}}>{taskModal.name}</div>
            {taskModal.description && <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'12px'}}>{taskModal.description}</div>}
            {taskModal.contact_name && (
              <div style={{padding:'8px 10px',background:'rgba(251,191,36,0.1)',border:'1px solid rgba(251,191,36,0.2)',borderRadius:'8px',marginBottom:'12px',fontSize:'13px'}}>
                <span style={{color:'#fbbf24',fontWeight:600}}>{taskModal.contact_name}</span>
                {taskModal.contact_phone && <span style={{color:'#fff',fontFamily:'monospace',marginLeft:'8px'}}>{taskModal.contact_phone}</span>}
              </div>
            )}
            <div style={{marginBottom:'12px'}}>
              <label style={{fontSize:'11px',fontWeight:600,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.08em',display:'block',marginBottom:'6px'}}>Notes</label>
              <textarea value={taskNotes} onChange={e => setTaskNotes(e.target.value)} rows={3}
                placeholder="Add any notes, observations..."
                style={{width:'100%',background:'rgba(255,255,255,0.07)',border:'1.5px solid rgba(255,255,255,0.1)',borderRadius:'10px',padding:'10px',fontSize:'14px',color:'#fff',resize:'none',boxSizing:'border-box',fontFamily:'inherit'}} />
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              <button disabled={taskSubmitting} onClick={async () => {
                setTaskSubmitting(true);
                try {
                  const validLogTypes = ['INCIDENT','PATROL','HEALTH_SAFETY','MEDICAL','VEHICLE_CHECK','CCTV_PATROL','GENERAL','WELFARE_CHECK','CCTV_CHECK','MANAGEMENT_VISIT','VISITOR','ACCESS_CONTROL','MAINTENANCE','HANDOVER','ALARM','FIRE_ALARM','EMERGENCY'];
                  const logType = validLogTypes.includes(taskModal.task_type) ? taskModal.task_type : 'GENERAL';
                  await api.logs.create({ site_id: site?.id, shift_id: shift?.id || null, log_type: logType, title: `✓ ${taskModal.name}`, description: taskNotes.trim() || `Task completed: ${taskModal.name}`, occurred_at: new Date().toISOString(), type_data: { scheduled_task_id: taskModal.id, task_type: taskModal.task_type } });
                  setCompletedTaskIds(prev => new Set([...prev, taskModal.id]));
                  setTaskModal(null);
                } catch (e) { alert(e.message); }
                finally { setTaskSubmitting(false); }
              }} style={{width:'100%',padding:'14px',background:'rgba(74,222,128,0.15)',border:'1.5px solid rgba(74,222,128,0.4)',borderRadius:'10px',color:'#4ade80',fontSize:'15px',fontWeight:700,cursor:'pointer'}}>
                {taskSubmitting ? 'Saving...' : '✓ TASK COMPLETE'}
              </button>
              <button disabled={taskSubmitting} onClick={async () => {
                if (!taskNotes.trim()) { alert('Please add a reason'); return; }
                setTaskSubmitting(true);
                try {
                  const validLogTypes = ['INCIDENT','PATROL','HEALTH_SAFETY','MEDICAL','VEHICLE_CHECK','CCTV_PATROL','GENERAL','WELFARE_CHECK','CCTV_CHECK','MANAGEMENT_VISIT','VISITOR','ACCESS_CONTROL','MAINTENANCE','HANDOVER','ALARM','FIRE_ALARM','EMERGENCY'];
                  const logType = validLogTypes.includes(taskModal.task_type) ? taskModal.task_type : 'GENERAL';
                  await api.logs.create({ site_id: site?.id, shift_id: shift?.id || null, log_type: logType, title: `✗ ${taskModal.name} — Unable to complete`, description: taskNotes.trim(), occurred_at: new Date().toISOString(), type_data: { scheduled_task_id: taskModal.id, task_type: taskModal.task_type, unable: true } });
                  setCompletedTaskIds(prev => new Set([...prev, taskModal.id]));
                  setTaskModal(null);
                } catch (e) { alert(e.message); }
                finally { setTaskSubmitting(false); }
              }} style={{width:'100%',padding:'12px',background:'rgba(239,68,68,0.1)',border:'1.5px solid rgba(239,68,68,0.3)',borderRadius:'10px',color:'#ef4444',fontSize:'13px',fontWeight:700,cursor:'pointer'}}>
                UNABLE TO COMPLETE
              </button>
              <button onClick={() => setTaskModal(null)}
                style={{width:'100%',padding:'10px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'rgba(255,255,255,0.4)',fontSize:'13px',cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last updated */}
      {lastUpdated && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.75rem'}}>
          <span style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.3)'}}>
            Updated {lastUpdated.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}
          </span>
          <button
            onClick={() => { setLoading(true); /* trigger re-fetch via state change */ setLastUpdated(null); }}
            style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.3)',background:'none',border:'none',cursor:'pointer',padding:0}}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Stats row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.625rem',marginBottom:'1.25rem'}}>
        <div className="officer-card" style={{textAlign:'center'}}>
          <div style={{fontSize:'1.75rem',fontWeight:700,color:'#fff'}}>{todayCount}</div>
          <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.06em',marginTop:'0.125rem'}}>Logs Today</div>
        </div>
        <div className="officer-card" style={{textAlign:'center'}}>
          <div style={{fontSize:'1.75rem',fontWeight:700,color:'#fff'}}>{tasks.length}</div>
          <div style={{fontSize:'0.6875rem',color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.06em',marginTop:'0.125rem'}}>Tasks Due</div>
        </div>
      </div>

      {/* Recent logs */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.625rem'}}>
        <div style={{fontSize:'0.6875rem',fontWeight:600,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.08em'}}>Recent Activity</div>
        <Link to="/logs" style={{fontSize:'0.75rem',color:'#60a5fa',textDecoration:'none',fontWeight:500}}>View All →</Link>
      </div>
      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner" style={{borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.15)'}} /></div>
      ) : recentLogs.length === 0 ? (
        <div style={{textAlign:'center',padding:'2rem',color:'rgba(255,255,255,0.3)',fontSize:'0.875rem'}}>No recent logs</div>
      ) : (
        recentLogs.filter(l => !l.type_data?.checkpoint).map((log) => <LogPreviewCard key={log.id} log={log} />)
      )}

      {/* Occurrence History */}
      <div style={{marginTop:'1.25rem',borderTop:'1px solid rgba(255,255,255,0.06)',paddingTop:'1rem'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.625rem'}}>
          <div style={{fontSize:'0.6875rem',fontWeight:600,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.08em'}}>Log History</div>
          <input type="date" value={historyDate} onChange={e => setHistoryDate(e.target.value)}
            style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'0.375rem 0.5rem',fontSize:'0.75rem',color:'#fff',fontFamily:'inherit'}} />
        </div>
        {historyDate && (
          historyLoading ? (
            <div style={{display:'flex',justifyContent:'center',padding:'1rem'}}><div className="spinner" style={{borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.15)',width:'1.25rem',height:'1.25rem'}} /></div>
          ) : historyLogs.length === 0 ? (
            <div style={{textAlign:'center',padding:'1rem',color:'rgba(255,255,255,0.3)',fontSize:'0.8125rem'}}>No logs for {new Date(historyDate + 'T12:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
          ) : (
            <>
              <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.3)',marginBottom:'0.5rem'}}>{historyLogs.length} log{historyLogs.length!==1?'s':''} on {new Date(historyDate + 'T12:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
              {historyLogs.map(log => <LogPreviewCard key={log.id} log={log} />)}
            </>
          )
        )}
      </div>
    </div>
  );
}

// Quick Action Button Component
function QuickActionButton({ to, icon, title, subtitle }) {
  return (
    <Link to={to} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.875rem',background:'#1a2235',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'8px',textDecoration:'none',color:'#fff',transition:'border-color 0.15s'}}
      onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'}
      onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'}
    >
      <span style={{color:'rgba(255,255,255,0.5)'}}>{icon}</span>
      <div>
        <div style={{fontSize:'0.875rem',fontWeight:600}}>{title}</div>
        {subtitle && <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)'}}>{subtitle}</div>}
      </div>
    </Link>
  );
}


function LogPreviewCard({ log }) {
  const typeMap = {
    PATROL:'PAT', INCIDENT:'INC', ALARM:'ALM', ACCESS:'ACC',
    VISITOR:'VIS', HANDOVER:'HND', MAINTENANCE:'MNT', VEHICLE:'VEH',
    KEYHOLDING:'KEY', GENERAL:'GEN',
  };
  const code = typeMap[log.log_type] || log.log_type?.slice(0,3) || 'LOG';
  return (
    <div className="officer-log-item">
      <div className="officer-log-type">{code}</div>
      <div style={{flex:1,minWidth:0}}>
        <div className="officer-log-title">{log.title || log.log_type || 'Log Entry'}</div>
        <div className="officer-log-meta">
          {log.site?.name && `${log.site.name} · `}
          {log.occurred_at ? new Date(log.occurred_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) : ''}
        </div>
      </div>
    </div>
  );
}


function TaskPreviewCard({ task }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.75rem',background:'rgba(255,255,255,0.05)',borderRadius:'8px'}}>
      <div style={{width:'2.25rem',height:'2.25rem',background:'rgba(26,82,168,0.3)',borderRadius:'5px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <ClipboardDocumentListIcon style={{width:'1rem',height:'1rem',color:'#60a5fa'}} />
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:'0.875rem',fontWeight:600,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.title}</div>
        {task.description && <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.description}</div>}
      </div>
    </div>
  );
}

export { OfficerApp };
